/**
 * Contacts API Service - TROIA V3
 * Service layer para comunicação com API de contatos
 */

import { apiGet, apiPost, apiPut, apiDelete, api } from '@/lib/api';
import type {
  ContactDevice,
  ContactResponse,
  ContactListResponse,
  CreateContactRequest,
  UpdateContactRequest,
  ContactQuery
} from '@troia-v3/types';

/**
 * Buscar lista de contatos com filtros e paginação
 */
export const getContacts = async (
  query?: ContactQuery,
  signal?: AbortSignal
): Promise<ContactListResponse> => {
  const params = new URLSearchParams();

  if (query?.page) params.append('page', query.page.toString());
  if (query?.limit) params.append('limit', query.limit.toString());
  if (query?.search) params.append('search', query.search);

  // Aplicar filtros
  if (query?.filters) {
    Object.entries(query.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(v => params.append(`filters[${key}]`, v.toString()));
        } else {
          params.append(`filters[${key}]`, value.toString());
        }
      }
    });
  }

  return apiGet<ContactListResponse>(
    `/contacts?${params.toString()}`,
    { signal }
  );
};

/**
 * Buscar contato específico por ID (usa rota dropdown - sem permissão de módulo)
 */
export const getContactById = async (
  id: string,
  signal?: AbortSignal
): Promise<ContactResponse> => {
  return apiGet<ContactResponse>(`/contacts/dropdown/${id}`, { signal });
};

/**
 * Dispositivos do contato (aba "Dispositivos" do drawer).
 *
 * Read-only: os registros nascem de eventos do widget (instalar o app,
 * permitir notificação) — não há escrita manual.
 */
export const getContactDevices = async (
  id: string,
  signal?: AbortSignal
): Promise<ContactDevice[]> => {
  return apiGet<ContactDevice[]>(`/contacts/${id}/devices`, { signal });
};

/**
 * Criar novo contato
 */
export const createContact = async (
  data: CreateContactRequest
): Promise<ContactResponse> => {
  return apiPost<ContactResponse>('/contacts', data);
};

/**
 * Criar novo contato com upload de arquivo (avatar)
 */
export const createContactWithFile = async (
  formData: FormData
): Promise<ContactResponse> => {
  const response = await api.post<{ success: boolean; data: ContactResponse }>('/contacts', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data.data;
};

/**
 * Atualizar contato existente
 */
export const updateContact = async (
  id: string,
  data: UpdateContactRequest
): Promise<ContactResponse> => {
  return apiPut<ContactResponse>(`/contacts/${id}`, data);
};

/**
 * Atualizar contato com upload de arquivo (avatar)
 */
export const updateContactWithFile = async (
  id: string,
  formData: FormData
): Promise<ContactResponse> => {
  const response = await api.put<{ success: boolean; data: ContactResponse }>(`/contacts/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data.data;
};

/**
 * Deletar contato
 */
export const deleteContact = async (id: string): Promise<void> => {
  return apiDelete(`/contacts/${id}`);
};

/**
 * Mergear dois contatos
 */
export const mergeContacts = async (
  sourceContactId: string,
  targetContactId: string,
  name: string
): Promise<ContactResponse> => {
  return apiPost<ContactResponse>('/contacts/merge', {
    sourceContactId,
    targetContactId,
    name
  });
};

/**
 * Buscar contato por email
 */
export const searchByEmail = async (
  email: string,
  signal?: AbortSignal
): Promise<ContactResponse | null> => {
  return apiGet<ContactResponse>(
    `/contacts/search/email/${encodeURIComponent(email)}`,
    { signal }
  );
};

/**
 * Buscar contato por telefone
 */
export const searchByPhone = async (
  phone: string,
  signal?: AbortSignal
): Promise<ContactResponse | null> => {
  return apiGet<ContactResponse>(
    `/contacts/search/phone/${encodeURIComponent(phone)}`,
    { signal }
  );
};

// Export como objeto para compatibilidade
export const contactsApi = {
  getContacts,
  getContactById,
  getContactDevices,
  createContact,
  createContactWithFile,
  updateContact,
  updateContactWithFile,
  deleteContact,
  mergeContacts,
  searchByEmail,
  searchByPhone
};
